module MonitoringRules {
    export class MonitoringRuleViewModel implements IMonitoringRuleData {
        constructor(public id?: number, data?: IMonitoringRuleData, series?: IRuleSeriesData[], alertsCount?: number) {
            if (data) {
                this.name = data.name;
                this.variableName = data.variableName;
                this.environmentName = data.environmentName
                this.expectedValue = data.expectedValue;
                this.expressionOperator = data.expressionOperator;
                this.expressionExpectedValue = data.expressionExpectedValue;
                this.aggregate = data.aggregate;
                this.operator = data.operator;
                this.timePeriod = data.timePeriod;
                this.timePeriodType = data.timePeriodType;
                this.timeZone = data.timeZone;
                this.expectedValue = data.expectedValue;
                this.chartData = series && this.getChartData(series);
                this.isActiveEveryDay = _.isUndefined(data.activeWeekDays) || _.isEmpty(data.activeWeekDays);
                this.activeWeekDays = data.activeWeekDays || [];
                this.isActiveEntireDay = _.isUndefined(data.startHour) || _.isNull(data.startHour);
                this.startHour = data.startHour;
                this.endHour = data.endHour;
                this.alertsCount = alertsCount
                this.chartOptions = this.getChartOptions(this.expectedValue);
                this.description = this.getDescription();
                this.seriesDataAvailable = series && !_.isEmpty(series);
                this.lastStatus = RuleStatus[this.getLastStatus(series)].toLowerCase();
            }
        }

        private static OPERATORS = {
            GreaterThan: ">",
            LessThan: "<",
            Equal: "=",
            Contains: FRENDS.Localizations.Contains
        }

        public name: string;
        public environmentName: string;
        public variableName: string;
        public aggregate: string;
        public operator: string;
        public timePeriod: number;
        public timePeriodType: string = "Minutes"; // TODO: enum?
        public timeZone: string;
        public isActiveEveryDay: boolean = true;
        public activeWeekDays: string[] = [];
        public isActiveEntireDay: boolean = true;
        public startHour: number;
        public endHour: number;
        public expectedValue: number;
        public expressionOperator: string = "Exists";
        public expressionExpectedValue: string | number;
        public chartData: IChartData;
        public alertsCount: number;
        public description: string;
        public chartOptions: IChartOptions;
        public seriesDataAvailable: boolean;
        public lastStatus: string;

        private getChartData(series: IRuleSeriesData[]): IChartData {
            let dataSeries = series.map(s => <IDataPoint>{ label: moment.utc(s.timestamp).toDate(), value: s.value });
            let alertSeries = series.map(s => <IDataPoint>{ label: moment.utc(s.timestamp).toDate(), value: !!s.alert ? (s.value || 0) : null });
            return {
                series: dataSeries,
                alertSeries: alertSeries
            };
        }

        public getActiveWeekdaysDescription() {
            return this.activeWeekDays.map(x => _.find(MonitoringRuleViewModel.WEEK_DAYS, d => d.value === x).name).join(',');
        }

        public static WEEK_DAYS = [
            {
                name: FRENDS.Localizations.Monday,
                value: "MON"
            },
            {
                name: FRENDS.Localizations.Tuesday,
                value: "TUE"
            },
            {
                name: FRENDS.Localizations.Wednesday,
                value: "WED"
            },
            {
                name: FRENDS.Localizations.Thursday,
                value: "THU"
            },
            {
                name: FRENDS.Localizations.Friday,
                value: "FRI"
            },
            {
                name: FRENDS.Localizations.Saturday,
                value: "SAT"
            },
            {
                name: FRENDS.Localizations.Sunday,
                value: "SUN"
            }
        ];


        private getChartOptions(expectedValue: number) {
            return {
                series: [
                    {
                        axis: "y",
                        dataset: "series",
                        key: "value",
                        color: "blue",
                        label: FRENDS.Localizations.Value,
                        type: ["line", "area"]
                    },
                    {
                        axis: "y",
                        dataset: "alertSeries",
                        key: "value",
                        color: "red",
                        label: `${FRENDS.Localizations.ExpectedValue} ${MonitoringRuleViewModel.OPERATORS[this.operator]} ${this.expectedValue}, ${FRENDS.Localizations.Was.toLowerCase()}`,
                        type: ["dot"],
                        defined: data => !_.isNull(data.y1)
                    }
                ],
                symbols: [{
                    type: 'hline',
                    value: expectedValue,
                    color: 'gray',
                    axis: "y"
                }],
                axes: {
                    x: {
                        key: "label",
                        type: "date",
                        padding: { max: 10 },
                        tickFormat: (value, index) => {
                            let d = moment(value);
                            if (d.minutes() === 0) {
                                if (d.hours() === 0) {
                                    return d.format("MMM D");
                                }
                                return d.format("LT");
                            }
                            return d.format("LT");
                        }
                    },
                    y: {
                        min: 0,
                        ticks: 4
                    }
                },
                grid: {
                    x: false,
                    y: true
                },
                margin: {
                    top: 40,
                    right: 0,
                    bottom: 20,
                    left: 70
                },
                tooltipHook: (data: IChartTooltipData[]) => {
                    if (data && data[0]) {
                        let rows = data.map(d => {
                            return {
                                label: d.series.label,
                                value: d.row.y1,
                                color: d.series.dataset === "alertSeries" ? "red" : "blue"
                            };
                        });
                        let tooltipDate = moment(data[0].row.x).format("MMMM DD, HH:mm");
                        return { abscissas: tooltipDate, rows: rows };
                    }
                }
            }
        }

        private getLastStatus(series: IRuleSeriesData[]) {
            if (!series || _.isEmpty(series)) {
                return RuleStatus.Unknown;
            }

            if (_.some(series, s => s.alert && !s.alert.acknowledged)) {
                return RuleStatus.Alert;
            }

            return RuleStatus.Ok;
        }

        private getDescription() {
            let timeperiodDescription = !this.isActiveEntireDay || !this.isActiveEveryDay ? FRENDS.Localizations.OnSelectedTimes.toLowerCase() : "";

            return `${FRENDS.Localizations.RuleForVariable} '${this.variableName}' ${timeperiodDescription} `;
        }
    }

    export interface IActiveWeekDayViewModel {
        name: string;
        value: string;
        selected: boolean;
    }

    export interface IMonitoringRuleData {
        name: string;
        environmentName: string;
        variableName: string;
        expressionOperator: string;
        expressionExpectedValue: string | number;
        aggregate: string;
        operator: string;
        timePeriod: number;
        timePeriodType: string;
        timeZone: string;
        expectedValue: number;
        isActiveEveryDay: boolean;
        activeWeekDays: string[];
        isActiveEntireDay: boolean;
        startHour: number;
        endHour: number;
    }

    export interface IRuleSeriesData {
        timestamp: string;
        value: number;
        alert: IRuleAlertData;
    }

    export interface IChartData {
        series: IDataPoint[];
        alertSeries: IDataPoint[];
    }

    interface IChartTooltipData {
        row: {
            x: Date,
            y0: number,
            y1: number
        },
        series: {
            axis: string,
            color: string,
            dataset: string,
            id: string,
            label: string,
            visible: string
        }
    }

    enum RuleStatus {
        Ok,
        Alert,
        Unknown
    }

    interface IChartOptions {
        [key: string]: any;
    }

    export interface IDataPoint {
        label: Date;
        value: number | string;
    }

    export interface IMonitoringRuleDataModel {
        id: number;
        environmentName: string;
        config: IMonitoringRuleData,
        series?: IRuleSeriesData[],
        alertsCount: number;
    }

    export interface IRuleAlertData {
        errorString: string;
        expectedValue: number;
        acknowledged: boolean;
        failedTimeWindowStartTime: string;
    }
}